import React, { useEffect, useMemo, useState } from "react";
import { authService } from "../services/auth.service";
import { vsixService, VsixVersion } from "../services/vsix.service";
import { IUser } from "../types";

const DownloadLanding: React.FC = () => {
  const [profile, setProfile] = useState<IUser | null>(null);
  const [error, setError] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle"
  );
  const [jiraOrgUrl, setJiraOrgUrl] = useState("");
  const [jiraUsername, setJiraUsername] = useState("");
  const [jiraAccessToken, setJiraAccessToken] = useState("");
  const [confluenceOrgUrl, setConfluenceOrgUrl] = useState("");
  const [confluenceUsername, setConfluenceUsername] = useState("");
  const [confluenceAccessToken, setConfluenceAccessToken] = useState("");
  const [showJiraToken, setShowJiraToken] = useState(false);
  const [showConfluenceToken, setShowConfluenceToken] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [integrationError, setIntegrationError] = useState("");
  const [vsixVersions, setVsixVersions] = useState<VsixVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAtlassianMcpHelp, setShowAtlassianMcpHelp] = useState(false);
  const [mcpCopyState, setMcpCopyState] = useState<{
    powershell: "idle" | "copied" | "failed";
    config: "idle" | "copied" | "failed";
  }>({ powershell: "idle", config: "idle", });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileResponse, versionsResponse] = await Promise.all([
          authService.getProfile(),
          vsixService.getVersions(),
        ]);
        setProfile(profileResponse.user);
        setVsixVersions(versionsResponse.versions);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!profile) {
      return;
    }
    const integrations = profile.settings?.integrations;
    const jira = integrations?.jira;
    const confluence = integrations?.confluence;
    setJiraOrgUrl(jira?.jiraOrgUrl || "");
    setJiraUsername(jira?.jiraUsername || profile.email || "");
    setJiraAccessToken(jira?.jiraAccessToken || "");
    setConfluenceOrgUrl(confluence?.confluenceOrgUrl || "");
    setConfluenceUsername(confluence?.confluenceUsername || profile.email || "");
    setConfluenceAccessToken(confluence?.confluenceAccessToken || "");
  }, [profile]);

  const displayName = useMemo(() => {
    if (!profile) return "there";
    const fullName = `${profile.firstName || ""} ${
      profile.lastName || ""
    }`.trim();
    return fullName || profile.email;
  }, [profile]);

  const handleCopy = async () => {
    if (!profile?.license?.key) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profile.license.key);
        setCopyState("copied");
        setTimeout(() => setCopyState("idle"), 2000);
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = profile.license.key;
      textArea.style.position = "fixed";
      textArea.style.left = "-999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch (copyError) {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      const blob = await vsixService.downloadVsix(filename);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to download file");
    }
  };

  const handleSaveIntegrations = async (event: React.FormEvent) => {
    event.preventDefault();
    setIntegrationError("");
    setIntegrationStatus("saving");

    try {
      const response = await authService.updateIntegrations(
        jiraOrgUrl.trim(),
        jiraUsername.trim(),
        jiraAccessToken.trim(),
        confluenceOrgUrl.trim(),
        confluenceUsername.trim(),
        confluenceAccessToken.trim()
      );
      setProfile((prev) =>
        prev ? { ...prev, settings: response.settings } : prev
      );
      setIntegrationStatus("saved");
      setTimeout(() => setIntegrationStatus("idle"), 2000);
    } catch (err: any) {
      setIntegrationStatus("error");
      setIntegrationError(
        err.response?.data?.message || "Failed to update integrations"
      );
    }
  };

  const handleCopyMcp = async (key: "powershell" | "config", text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setMcpCopyState((prev) => ({ ...prev, [key]: "copied" }));
      setTimeout(
        () => setMcpCopyState((prev) => ({ ...prev, [key]: "idle" })),
        2000
      );
    } catch (copyError) {
      setMcpCopyState((prev) => ({ ...prev, [key]: "failed" }));
      setTimeout(
        () => setMcpCopyState((prev) => ({ ...prev, [key]: "idle" })),
        2000
      );
    }
  };

  return (
    <div className="downloads-page">
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .downloads-page {
          --ink: #0f172a;
          --muted: #5f6b7a;
          --accent: #ef6f42;
          --accent-deep: #d85830;
          --accent-cool: #1fbba6;
          --panel: #ffffff;
          --panel-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
          --stroke: rgba(15, 23, 42, 0.08);
          --bg: #f7f0e8;
          font-family: 'Space Grotesk', 'Segoe UI', sans-serif;
          color: var(--ink);
          position: relative;
          min-height: calc(100vh - 120px);
          padding: 32px;
          border-radius: 28px;
          background:
            radial-gradient(circle at top right, rgba(31, 187, 166, 0.25), transparent 55%),
            radial-gradient(circle at 10% 20%, rgba(239, 111, 66, 0.28), transparent 60%),
            var(--bg);
          overflow: hidden;
        }

        .downloads-orb {
          position: absolute;
          border-radius: 999px;
          filter: blur(0.5px);
          opacity: 0.55;
          pointer-events: none;
        }

        .downloads-orb.one {
          width: 240px;
          height: 240px;
          background: rgba(31, 187, 166, 0.35);
          top: -80px;
          right: -40px;
        }

        .downloads-orb.two {
          width: 180px;
          height: 180px;
          background: rgba(239, 111, 66, 0.25);
          bottom: 40px;
          left: -60px;
        }

        .downloads-hero {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
          gap: 32px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: clamp(2.2rem, 4vw, 3.4rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 16px;
        }

        .hero-title span {
          color: var(--accent);
        }

        .hero-copy {
          font-size: 1.05rem;
          line-height: 1.7;
          color: var(--muted);
          margin-bottom: 24px;
        }

        .hero-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 24px;
        }

        .hero-badge {
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.08);
          font-size: 0.85rem;
          font-weight: 600;
        }

        .hero-steps {
          background: var(--panel);
          padding: 20px;
          border-radius: 18px;
          box-shadow: var(--panel-shadow);
          border: 1px solid var(--stroke);
        }

        .hero-steps h3 {
          margin: 0 0 12px;
          font-size: 1rem;
          font-weight: 600;
        }

        .steps-list {
          display: grid;
          gap: 10px;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .steps-list li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 0.95rem;
          color: var(--muted);
        }

        .steps-list span {
          background: var(--accent);
          color: #fff;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 0.8rem;
        }

        .license-card {
          background: var(--panel);
          padding: 24px;
          border-radius: 20px;
          box-shadow: var(--panel-shadow);
          border: 1px solid var(--stroke);
          display: grid;
          gap: 16px;
        }

        .license-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .license-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .license-pill {
          padding: 6px 12px;
          border-radius: 999px;
          background: rgba(31, 187, 166, 0.18);
          color: #0b594f;
          font-size: 0.78rem;
          font-weight: 600;
        }

        .license-key {
          font-family: 'IBM Plex Mono', 'Courier New', monospace;
          font-size: 0.95rem;
          padding: 12px 16px;
          background: #0f172a;
          color: #f8fafc;
          border-radius: 12px;
          letter-spacing: 0.6px;
          word-break: break-all;
        }

        .license-meta {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 0.9rem;
          color: var(--muted);
        }

        .integration-card {
          background: var(--panel);
          border-radius: 20px;
          padding: 24px;
          box-shadow: var(--panel-shadow);
          border: 1px solid var(--stroke);
          display: grid;
          gap: 16px;
        }

        .integration-help {
          display: grid;
          gap: 6px;
          padding: 16px 18px;
          margin-bottom: 10px;
          border-radius: 16px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: linear-gradient(
            135deg,
            rgba(31, 187, 166, 0.12),
            rgba(239, 111, 66, 0.1)
          );
        }

        .integration-help-title {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--ink);
        }

        .integration-help p {
          margin: 0;
          color: var(--muted);
          font-size: 0.9rem;
        }

        .integration-help a {
          color: var(--accent-cool);
          font-weight: 600;
          text-decoration: none;
        }

        .integration-help a:hover {
          text-decoration: underline;
        }

        .integration-help-actions {
          margin-top: 12px;
        }

        .integration-help-button {
          padding: 10px 18px;
          border-radius: 999px;
          border: none;
          background: linear-gradient(135deg, #ef6f42, #f4a261);
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 12px 24px rgba(239, 111, 66, 0.25);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .integration-help-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 16px 30px rgba(239, 111, 66, 0.35);
        }

        .mcp-modal-overlay {
          position: fixed;
          inset: 0;
          background:
            radial-gradient(circle at top left, rgba(239, 111, 66, 0.25), transparent 45%),
            radial-gradient(circle at bottom right, rgba(31, 187, 166, 0.3), transparent 50%),
            rgba(15, 23, 42, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 50;
        }

        .mcp-modal {
          position: relative;
          background:
            radial-gradient(circle at 110% 10%, rgba(239, 111, 66, 0.18), transparent 45%),
            radial-gradient(circle at -10% 90%, rgba(31, 187, 166, 0.18), transparent 40%),
            #ffffff;
          border-radius: 24px;
          max-width: 980px;
          width: 100%;
          padding: 28px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 30px 80px rgba(15, 23, 42, 0.2);
        }

        .mcp-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .mcp-modal-title {
          font-size: 1.6rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .mcp-title-icon {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          background: linear-gradient(135deg, rgba(239, 111, 66, 0.2), rgba(31, 187, 166, 0.25));
          display: grid;
          place-items: center;
        }

        .mcp-title-icon svg {
          width: 20px;
          height: 20px;
        }

        .mcp-modal-close {
          border: none;
          background: #fff;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-size: 1.1rem;
          cursor: pointer;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
          color: var(--muted);
          transition: transform 0.15s ease, color 0.2s ease;
        }

        .mcp-modal-close:hover {
          transform: translateY(-1px);
          color: var(--accent);
        }

        .mcp-steps {
          display: grid;
          gap: 16px;
          margin: 0;
          padding-left: 18px;
          color: var(--muted);
          font-size: 0.98rem;
          line-height: 1.6;
        }

        .mcp-code {
          background: linear-gradient(135deg, #0f172a, #111827);
          color: #f8fafc;
          border-radius: 14px;
          padding: 18px;
          font-family: 'IBM Plex Mono', 'SFMono-Regular', monospace;
          font-size: 0.9rem;
          overflow-x: auto;
          border: 1px solid rgba(248, 250, 252, 0.08);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
        }

        .mcp-code::selection {
          background: rgba(239, 111, 66, 0.6);
        }

        .mcp-code-block {
          position: relative;
          margin-top: 12px;
        }

        .mcp-copy-button {
          position: absolute;
          right: 12px;
          top: 12px;
          background: rgba(255, 255, 255, 0.12);
          color: #f8fafc;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 999px;
          padding: 6px 8px;
          font-size: 0.78rem;
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(6px);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }

        .mcp-copy-button:hover {
          border-color: rgba(255, 255, 255, 0.5);
          transform: translateY(-1px);
        }

        .mcp-copy-button svg {
          width: 16px;
          height: 16px;
          stroke: currentColor;
          display: block;
        }

        .mcp-copy-status {
          margin-top: 6px;
          font-size: 0.8rem;
          color: var(--muted);
        }

        .integration-form {
          display: grid;
          gap: 16px;
        }

        .integration-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
        }

        .integration-group {
          display: grid;
          gap: 12px;
          padding: 16px;
          border-radius: 16px;
          background: rgba(15, 23, 42, 0.03);
          border: 1px dashed rgba(15, 23, 42, 0.1);
        }

        .integration-group-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .integration-group-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .integration-group-copy {
          margin: 0;
          color: var(--muted);
          font-size: 0.85rem;
        }

        .integration-field label {
          display: block;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .integration-input {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.1);
          border-radius: 12px;
          padding: 8px 12px;
        }

        .integration-input input {
          border: none;
          background: transparent;
          font-size: 0.95rem;
          flex: 1;
          outline: none;
          color: var(--ink);
        }

        .integration-toggle {
          border: none;
          background: rgba(15, 23, 42, 0.08);
          color: var(--ink);
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
        }

        .integration-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 20px;
        }

        .integration-save {
          border: none;
          border-radius: 12px;
          padding: 10px 18px;
          font-weight: 600;
          background: var(--accent-cool);
          color: #fff;
          cursor: pointer;
        }

        .integration-status {
          font-size: 0.85rem;
          color: var(--muted);
        }

        .integration-error {
          color: #b91c1c;
          font-size: 0.85rem;
        }

        .copy-button {
          border: none;
          border-radius: 999px;
          padding: 10px 18px;
          font-weight: 600;
          background: var(--accent);
          color: #fff;
          cursor: pointer;
          transition: transform 0.2s ease, background 0.2s ease;
        }

        .copy-button:hover {
          transform: translateY(-1px);
          background: var(--accent-deep);
        }

        .copy-status {
          font-size: 0.85rem;
          color: var(--muted);
        }

        .downloads-section {
          margin-top: 40px;
          position: relative;
          z-index: 1;
        }

        .section-title {
          font-size: 1.4rem;
          margin-bottom: 8px;
        }

        .section-subtitle {
          color: var(--muted);
          margin-bottom: 20px;
        }

        .downloads-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
        }

        .download-card {
          background: var(--panel);
          border-radius: 18px;
          padding: 20px;
          box-shadow: var(--panel-shadow);
          border: 1px solid var(--stroke);
          display: grid;
          gap: 12px;
        }

        .download-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .download-version {
          font-size: 1.15rem;
          font-weight: 600;
        }

        .latest-badge {
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(239, 111, 66, 0.2);
          color: #a33d22;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .download-meta {
          font-size: 0.85rem;
          color: var(--muted);
        }

        .download-notes {
          color: var(--muted);
          line-height: 1.5;
          font-size: 0.9rem;
        }

        .download-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .download-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: #fff;
          background: var(--accent-cool);
          padding: 10px 16px;
          border-radius: 12px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          font-family: 'Space Grotesk', 'Segoe UI', sans-serif;
        }

        .download-link:hover {
          opacity: 0.9;
        }

        .download-size {
          font-size: 0.8rem;
          color: var(--muted);
        }

        .error-banner {
          margin-top: 16px;
          padding: 12px 16px;
          border-radius: 12px;
          background: rgba(239, 68, 68, 0.15);
          color: #b91c1c;
          font-size: 0.9rem;
        }

        @media (max-width: 900px) {
          .downloads-hero {
            grid-template-columns: 1fr;
          }
        }
      `}
      </style>

      <div className="downloads-orb one" />
      <div className="downloads-orb two" />

      <section className="downloads-hero">
        <div>
          <h1 className="hero-title">
            Hi {displayName}, your VS Code extension is ready to{" "}
            <span>install</span>.
          </h1>
          <p className="hero-copy">
            Download the VSIX file below, install it in VS Code, and enter your
            license key to activate the extension. Keep this page handy for
            updates and renewals.
          </p>
          <div className="hero-badges">
            <div className="hero-badge">Secure install</div>
            <div className="hero-badge">Manual VSIX package</div>
            <div className="hero-badge">License required</div>
          </div>
          <div className="hero-steps">
            <h3>Setup steps</h3>
            <ul className="steps-list">
              <li>
                <span>1</span>Download a VSIX version below.
              </li>
              <li>
                <span>2</span>In VS Code, open Extensions &gt; Install from
                VSIX.
              </li>
              <li>
                <span>3</span>Click SignIn to continue.
              </li>
            </ul>
          </div>
        </div>

        <div className="license-card">
          <div className="license-header">
            <h2 className="license-title">Your license key</h2>
            <div className="license-pill">Active</div>
          </div>
          <div className="license-key">
            {profile?.license?.key || "Loading license..."}
          </div>
          <div className="license-meta">
            <div>
              <strong>Valid until:</strong>{" "}
              {profile?.license?.validUntilDate
                ? new Date(profile.license.validUntilDate).toLocaleDateString()
                : "Loading..."}
            </div>
            <button className="copy-button" type="button" onClick={handleCopy}>
              {copyState === "copied" ? "Copied" : "Copy key"}
            </button>
          </div>
          <div className="copy-status">
            {copyState === "failed" &&
              "Copy failed. Please select the key manually."}
          </div>
        </div>
      </section>

      <section className="downloads-section">
        <h2 className="section-title">Atlassian integrations</h2>
        <p className="section-subtitle">
          Store your Jira and Confluence org URLs, usernames, and access tokens
          securely for future integrations.
        </p>
        <div className="integration-help">
          <p className="integration-help-title">
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noreferrer"
            >
              Click here
            </a>{" "}
            to get your API token.
          </p>
          <div className="integration-help-actions">
            <button
              type="button"
              className="integration-help-button"
              onClick={() => setShowAtlassianMcpHelp(true)}
            >
              Show MCP setup steps
            </button>
          </div>
        </div>
        <div className="integration-card">
          <form className="integration-form" onSubmit={handleSaveIntegrations}>
            <div className="integration-group">
              <div className="integration-group-header">
                <h3 className="integration-group-title">Jira</h3>
              </div>
              <div className="integration-grid">
                <div className="integration-field">
                  <label htmlFor="jira-org-url">Org URL</label>
                  <div className="integration-input">
                    <input
                      id="jira-org-url"
                      type="url"
                      value={jiraOrgUrl}
                      onChange={(event) => setJiraOrgUrl(event.target.value)}
                      placeholder="https://your-org.atlassian.net"
                    />
                  </div>
                </div>
                <div className="integration-field">
                  <label htmlFor="jira-username">Username or email</label>
                  <div className="integration-input">
                    <input
                      id="jira-username"
                      type="text"
                      value={jiraUsername}
                      onChange={(event) => setJiraUsername(event.target.value)}
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div className="integration-field">
                  <label htmlFor="jira-token">Access token</label>
                  <div className="integration-input">
                    <input
                      id="jira-token"
                      type={showJiraToken ? "text" : "password"}
                      value={jiraAccessToken}
                      onChange={(event) =>
                        setJiraAccessToken(event.target.value)
                      }
                      placeholder="Paste Jira access token"
                    />
                    <button
                      type="button"
                      className="integration-toggle"
                      onClick={() => setShowJiraToken(!showJiraToken)}
                    >
                      {showJiraToken ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="integration-group">
              <div className="integration-group-header">
                <h3 className="integration-group-title">Confluence</h3>
              </div>
              <div className="integration-grid">
                <div className="integration-field">
                  <label htmlFor="confluence-org-url">Org URL</label>
                  <div className="integration-input">
                    <input
                      id="confluence-org-url"
                      type="url"
                      value={confluenceOrgUrl}
                      onChange={(event) =>
                        setConfluenceOrgUrl(event.target.value)
                      }
                      placeholder="https://your-org.atlassian.net/wiki"
                    />
                  </div>
                </div>
                <div className="integration-field">
                  <label htmlFor="confluence-username">Username or email</label>
                  <div className="integration-input">
                    <input
                      id="confluence-username"
                      type="text"
                      value={confluenceUsername}
                      onChange={(event) =>
                        setConfluenceUsername(event.target.value)
                      }
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div className="integration-field">
                  <label htmlFor="confluence-token">Access token</label>
                  <div className="integration-input">
                    <input
                      id="confluence-token"
                      type={showConfluenceToken ? "text" : "password"}
                      value={confluenceAccessToken}
                      onChange={(event) =>
                        setConfluenceAccessToken(event.target.value)
                      }
                      placeholder="Paste Confluence access token"
                    />
                    <button
                      type="button"
                      className="integration-toggle"
                      onClick={() =>
                        setShowConfluenceToken(!showConfluenceToken)
                      }
                    >
                      {showConfluenceToken ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="integration-actions">
              <button
                type="submit"
                className="integration-save"
                disabled={integrationStatus === "saving"}
              >
                {integrationStatus === "saving" ? "Saving..." : "Save"}
              </button>
              {integrationStatus === "saved" && (
                <span className="integration-status">Saved</span>
              )}
              {integrationStatus === "error" && (
                <span className="integration-error">
                  {integrationError || "Failed to save"}
                </span>
              )}
            </div>
          </form>
        </div>
        {showAtlassianMcpHelp && (
          <div
            className="mcp-modal-overlay"
            onClick={() => setShowAtlassianMcpHelp(false)}
          >
            <div
              className="mcp-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mcp-modal-header">
                <h3 className="mcp-modal-title">
                  <span className="mcp-title-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#ef6f42" strokeWidth="2">
                      <path d="M12 3l7 4v6c0 5-7 8-7 8s-7-3-7-8V7l7-4z" />
                      <path d="M9 12h6" />
                      <path d="M12 9v6" />
                    </svg>
                  </span>
                  Atlassian MCP setup in VS Code
                </h3>
                <button
                  type="button"
                  className="mcp-modal-close"
                  onClick={() => setShowAtlassianMcpHelp(false)}
                  aria-label="Close"
                >
                  x
                </button>
              </div>
              <ol className="mcp-steps">
                <li>
                  Run this command in PowerShell and replace the username and
                  API token with your Atlassian username and API token (create
                  the token at{" "}
                  <a
                    href="https://id.atlassian.com/manage-profile/security/api-tokens"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://id.atlassian.com/manage-profile/security/api-tokens
                  </a>
                  ):
                  <div className="mcp-code-block">
                    <button
                      type="button"
                      className="mcp-copy-button"
                      onClick={() =>
                        handleCopyMcp(
                          "powershell",
                          '[Convert]::ToBase64String([Text.Encoding]::UTF8.GetBytes("user@example.com:api_token"))'
                        )
                      }
                      aria-label={
                        mcpCopyState.powershell === "copied"
                          ? "Copied"
                          : "Copy command"
                      }
                    >
                      {mcpCopyState.powershell === "copied" ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                    <pre className="mcp-code">
[Convert]::ToBase64String([Text.Encoding]::UTF8.GetBytes("user@example.com:api_token"))
                    </pre>
                    {mcpCopyState.powershell === "failed" && (
                      <div className="mcp-copy-status">
                        Copy failed. Please select the command manually.
                      </div>
                    )}
                  </div>
                </li>
                <li>
                  Copy the output from PowerShell and paste it into the MCP
                  server config in the VS Code extension (replace only the
                  XXXX portion in Authorization "Basic XXXXX" with the
                  output; keep the word "Basic"):
                  <div className="mcp-code-block">
                    <button
                      type="button"
                      className="mcp-copy-button"
                      onClick={() =>
                        handleCopyMcp(
                          "config",
                          `{
  "mcpServers": {
    "atlassian-cloud": {
      "autoApprove": [],
      "disabled": false,
      "timeout": 60,
      "type": "streamableHttp",
      "url": "https://atlassianmcp.eidosverse.com/mcp",
      "headers": {
        "Authorization": "Basic XXXXX"
      }
    }
  }
}`
                        )
                      }
                      aria-label={
                        mcpCopyState.config === "copied"
                          ? "Copied"
                          : "Copy config"
                      }
                    >
                      {mcpCopyState.config === "copied" ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                    <pre className="mcp-code">
{`{
  "mcpServers": {
    "atlassian-cloud": {
      "autoApprove": [],
      "disabled": false,
      "timeout": 60,
      "type": "streamableHttp",
      "url": "https://atlassianmcp.eidosverse.com/mcp",
      "headers": {
        "Authorization": "Basic XXXXX"
      }
    }
  }
}`}
                    </pre>
                    {mcpCopyState.config === "failed" && (
                      <div className="mcp-copy-status">
                        Copy failed. Please select the config manually.
                      </div>
                    )}
                  </div>
                </li>
              </ol>
            </div>
          </div>
        )}
      </section>

      <section className="downloads-section">
        <h2 className="section-title">Download VSIX versions</h2>
        <p className="section-subtitle">
          Choose the version you want to install. Latest is recommended for new
          setups.
        </p>
        <div className="downloads-grid">
          {loading ? (
            <div>Loading versions...</div>
          ) : vsixVersions.length === 0 ? (
            <div>No versions available</div>
          ) : (
            vsixVersions.map((item) => (
              <div key={item.version} className="download-card">
                <div className="download-header">
                  <div className="download-version">{item.version}</div>
                  {item.latest && <div className="latest-badge">Latest</div>}
                </div>
                <div className="download-meta">Released {item.releasedOn}</div>
                <div className="download-notes">{item.highlights}</div>
                <div className="download-actions">
                  <button
                    className="download-link"
                    onClick={() => handleDownload(item.filename)}
                    type="button"
                  >
                    Download VSIX
                  </button>
                  <div className="download-size">{item.size}</div>
                </div>
              </div>
            ))
          )}
        </div>
        {error && <div className="error-banner">{error}</div>}
      </section>
    </div>
  );
};

export default DownloadLanding;
